import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  StructuredListWrapper,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
} from "@carbon/react";
import { IT_DOESNT_HAVE_SPEC, TO_BE_AGREED } from "../../../utils";
import { MaterialType, MaterialResponse } from "../../../types";
import { FileReader } from "../..";
import { useLanguage } from "../../../hooks";
import { getTagTextColor } from "./tagContrastColor";
import "./detail.scss";

type DetailProps = {
  material: MaterialResponse;
  pnType: string;
};

/**
 * The material detail field list — a Carbon `StructuredList` of label/value rows. Replaced the
 * pre-Carbon `InfoItem`, then a bespoke `<dl>` grid; a `StructuredList` reads as a proper,
 * bordered key/value table rather than a loose grid. Every field, its visibility condition and its
 * order are unchanged.
 *
 * Per-material tags render as small colour-swatch chips (colour is DATA, arbitrary per tag) — see
 * `tagContrastColor.ts` for why the chip's own text-colour logic is copied rather than imported.
 * `owner`/`user` render from `fullName`, matching what these two fields always rendered.
 */
const Detail = ({ material, pnType }: DetailProps) => {
  const { t } = useTranslation();
  const langEs = useLanguage();

  const isGood =
    pnType === MaterialType.DIRECT_GOODS ||
    pnType === MaterialType.INDIRECT_GOODS;

  const rows: { label: string; value: ReactNode; show?: boolean }[] = [
    {
      label: t("material.merchancyCode"),
      value: langEs ? material?.category?.name : material?.category?.nameEn,
    },
    { label: t("material.measurementUnit"), value: material?.measurementUnit },
    {
      label: t("material.deliveryFormat"),
      value: material?.deliveryFormat,
      show: Boolean(material?.deliveryFormat),
    },
    {
      label: t("material.deliveryPlace"),
      value: material?.deliveryPlace?.name ?? TO_BE_AGREED,
    },
    {
      label: t("material.certifications"),
      value: material?.certifications?.map((cert) => cert.name).join(", "),
      show: material?.certifications?.length > 0,
    },
    {
      label: t("tags.tags"),
      value: (
        <div className="material-detail__tags">
          {material?.tags?.map((tag) => {
            // A tag may have no colour; fall back to a neutral swatch so it still renders (and
            // getTagTextColor no longer crashes on an undefined colour).
            const tagColor = tag.color || "#e0e0e0";
            return (
              <span
                key={tag.id}
                className="material-detail__tag"
                style={{
                  backgroundColor: tagColor,
                  color: getTagTextColor(tagColor),
                }}
              >
                {tag.name}
              </span>
            );
          })}
        </div>
      ),
      show: material?.tags?.length > 0,
    },
    {
      label: isGood ? t("material.brand") : "Marca / Producto",
      value: material?.brand,
      show: Boolean(material?.brand),
    },
    {
      label: t("material.comments"),
      value: material?.comments,
      show: Boolean(material?.comments),
    },
    {
      label: t("newMaterialForm.details"),
      value: material?.details,
      show: Boolean(material?.details),
    },
    {
      label:
        pnType === MaterialType.SERVICES
          ? t("material.confidentService")
          : t("material.confidentMaterial"),
      value: material?.confident ? t("cta.yes") : t("cta.no"),
    },
    {
      label: t("material.confidential"),
      value: material?.confidentSpec ? t("cta.yes") : t("cta.no"),
    },
    {
      label: t("material.specification"),
      value: material?.fileURL ? (
        <FileReader fileKey={material.fileURL} />
      ) : (
        IT_DOESNT_HAVE_SPEC
      ),
    },
    { label: t("material.responsible"), value: material?.owner?.fullName },
    { label: t("material.owner"), value: material?.user?.fullName },
  ];

  return (
    <StructuredListWrapper
      aria-label={t("material.detail")}
      className="material-detail"
    >
      <StructuredListBody>
        {rows
          .filter((row) => row.show !== false)
          .map((row) => (
            <StructuredListRow key={row.label}>
              <StructuredListCell head className="material-detail__label">
                {row.label}
              </StructuredListCell>
              <StructuredListCell>{row.value}</StructuredListCell>
            </StructuredListRow>
          ))}
      </StructuredListBody>
    </StructuredListWrapper>
  );
};

export default Detail;
